import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import { eventService } from '../services/api';
import { formatDate, formatDateTime } from '../utils/helpers';
import { getSlugFromParams, getEventEditUrl } from '../utils/urlHelpers';
import GoogleMapsLocationLink from '../components/common/GoogleMapsLocationLink';
import ClickableImageGrid from '../components/common/ClickableImageGrid';
import { StarIcon } from '@heroicons/react/24/solid';
import { canManageOwnedResource } from '../utils/resourcePermissions';
import useImageLightbox from '../hooks/useImageLightbox';
import { normalizeGalleryImages } from '../utils/imageLightbox';

const EventDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const { openLightbox, Lightbox } = useImageLightbox();

  const eventIdentifier = getSlugFromParams(params, 'event');
  const societySlug = params.societySlug;
  const eventGallerySlides = useMemo(
    () => normalizeGalleryImages(event?.images, event?.name || 'Event'),
    [event?.images, event?.name],
  );

  const eventScheduleLabel = useMemo(() => {
    if (!event) return '';
    if (event.date_time) {
      return formatDateTime(event.date_time);
    }
    if (event.start_date) {
      const datePart = formatDate(event.start_date);
      const timePart = event.start_time ? String(event.start_time).slice(0, 5) : '';
      return timePart ? `${datePart} at ${timePart}` : datePart;
    }
    return 'Date to be announced';
  }, [event]);

  useEffect(() => {
    if (eventIdentifier) {
      loadEvent();
      loadRelatedEvents();
      incrementViews();
    }
  }, [eventIdentifier]);

  useEffect(() => {
    if (eventIdentifier) loadReviews();
  }, [eventIdentifier]);

  const loadReviews = async () => {
    if (!eventIdentifier) return;
    try {
      const response = await eventService.getEventReviews(eventIdentifier);
      setReviews(response.results || response || []);
    } catch (err) {
      console.error('Error loading event reviews:', err);
      setReviews([]);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/events/${eventIdentifier}` } });
      return;
    }
    if (!eventIdentifier) return;
    setSubmittingReview(true);
    try {
      await eventService.createEventReview(eventIdentifier, reviewForm);
      await loadReviews();
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
    } catch (err) {
      console.error('Error submitting review:', err);
      if (err.response?.status === 401) {
        navigate('/login', { state: { from: `/events/${eventIdentifier}` } });
      } else {
        alert('Failed to submit comment. Please try again.');
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (count) =>
    [...Array(5)].map((_, i) => (
      <StarIcon
        key={i}
        className={`h-5 w-5 ${i < count ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));

  const loadEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const eventData = await eventService.getEvent(eventIdentifier);
      setEvent(eventData);
      setViews(eventData.views || 0);
    } catch (err) {
      console.error('Error loading event:', err);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedEvents = async () => {
    try {
      const response = await eventService.getEvents({ 
        category: event?.category,
        page_size: 6,
        exclude: eventIdentifier
      });
      setRelatedEvents(response.results || response);
    } catch (err) {
      console.error('Error loading related events:', err);
    }
  };

  const incrementViews = async () => {
    try {
      await eventService.incrementViews(eventIdentifier);
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  const handleEdit = () => {
    navigate(
      getEventEditUrl(
        event || { slug: eventIdentifier, id: eventIdentifier },
        societySlug || event?.society?.slug,
      ),
    );
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await eventService.deleteEvent(eventIdentifier);
        navigate('/events');
      } catch (err) {
        console.error('Error deleting event:', err);
        alert('Failed to delete event');
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
      setSendingMessage(true);
      // TODO: Implement message sending
      console.log('Sending message:', message);
      alert('Message sent successfully!');
      setMessage('');
      setShowMessageForm(false);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const canManage = user && event && canManageOwnedResource(user, event);

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading event details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/events')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Events
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!event) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
            <p className="text-gray-600 mb-6">The event you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/events')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Events
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`${event.name} - PakPoints`}>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-6">
            {/* Left Sidebar - Removed ads to avoid navigation confusion */}

            {/* Main Content Area - Full Width */}
            <div className="flex-1 min-w-0">
              <div className="max-w-7xl mx-auto">
                {/* Top Ad - Removed to avoid header confusion */}

                {/* Main Content - Full Width */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {/* Event Images */}
            <div className="aspect-w-16 aspect-h-9">
              <div className="w-full h-96 bg-gray-200 flex items-center justify-center">
                {event.images && event.images.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => openLightbox(eventGallerySlides, 0)}
                    className="w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                    aria-label="View full size image"
                  >
                    <img
                      src={event.images[0].original_image}
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ) : (
                  <div className="text-center text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p>No image available</p>
                  </div>
                )}
              </div>
            </div>

            {event.images && event.images.length > 1 && (
              <ClickableImageGrid
                images={event.images}
                title="Photos"
                namePrefix={event.name}
                showCard={false}
                gridClassName="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border-t border-gray-200"
                imageClassName="w-full h-24 object-cover rounded-lg"
              />
            )}

            {/* Event Content */}
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{event.name}</h1>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {eventScheduleLabel}
                    </span>
                    <span>•</span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {views} views
                    </span>
                    <span>•</span>
                    <span className="capitalize">{event.category}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-blue-600">{event.venue}</div>
                  <div className="text-sm text-gray-500">Venue</div>
                </div>
              </div>

              {/* Event Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Event Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                    <p className="font-medium">{eventScheduleLabel}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Host</p>
                    <p className="font-medium">{event.host || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Special Guest</p>
                    <p className="font-medium">{event.special_guest || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Society</p>
                    <p className="font-medium">{event.society?.name || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{event.details}</p>
              </div>

              <GoogleMapsLocationLink
                className="mb-6"
                variant="card"
                heading="Location"
                latitude={event.latitude}
                longitude={event.longitude}
                venue={event.venue}
                city={event.city}
                name={event.name}
              />

              {/* Contact Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Event Information</h3>
                <div className="space-y-2">
                  <p><span className="font-medium">Organizer:</span> {event.user?.full_name || event.user?.first_name || 'Unknown'}</p>
                  <p><span className="font-medium">Venue:</span> {event.venue}</p>
                  {event.contact_phone && (
                    <p>
                      <span className="font-medium">Contact:</span>{' '}
                      <a 
                        href={`tel:${event.contact_phone}`}
                        className="text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {event.contact_phone}
                      </a>
                    </p>
                  )}
                  {event.user?.email && (
                    <p>
                      <span className="font-medium">Email:</span>{' '}
                      <a 
                        href={`mailto:${event.user.email}`}
                        className="text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {event.user.email}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                {canManage ? (
                  <>
                    <button
                      onClick={handleEdit}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Edit Event
                    </button>
                    <button
                      onClick={handleDelete}
                      className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Delete Event
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowMessageForm(!showMessageForm)}
                    className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    {showMessageForm ? 'Cancel' : 'Contact Organizer'}
                  </button>
                )}
              </div>

              {/* Message Form */}
              {showMessageForm && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Message to Organizer</h3>
                  <form onSubmit={handleSendMessage}>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your message here..."
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                      rows={4}
                      required
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={sendingMessage}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {sendingMessage ? 'Sending...' : 'Send Message'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowMessageForm(false)}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Comments / Reviews section */}
              <section className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Comments ({reviews.length})</h2>
                  {isAuthenticated && user && (
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(!showReviewForm)}
                      className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      {showReviewForm ? 'Cancel' : 'Add comment'}
                    </button>
                  )}
                </div>

                {showReviewForm && user && (
                  <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <form onSubmit={handleReviewSubmit}>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              className="focus:outline-none"
                            >
                              <StarIcon
                                className={`h-6 w-6 ${star <= reviewForm.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                              />
                            </button>
                          ))}
                          <span className="ml-2 text-sm text-gray-600">{reviewForm.rating} out of 5</span>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title (optional)</label>
                        <input
                          type="text"
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Give your review a title"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Comment</label>
                        <textarea
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Share your experience..."
                        />
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={submittingReview}
                          className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submittingReview ? 'Submitting...' : 'Submit comment'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {!isAuthenticated && (
                  <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-sm text-primary-800">
                      Please <Link to="/login" state={{ from: `/events/${eventIdentifier}` }} className="underline font-medium">log in</Link> to add a comment.
                    </p>
                  </div>
                )}

                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">{review.user_name || 'Anonymous'}</h4>
                            <div className="flex items-center gap-1 mt-1">{renderStars(review.rating)}</div>
                          </div>
                          <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                        </div>
                        {review.title && <h5 className="font-medium text-gray-800 mb-1">{review.title}</h5>}
                        {review.comment && <p className="text-gray-700 text-sm">{review.comment}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-6 text-gray-500">No comments yet. Be the first to share your experience!</p>
                )}
              </section>
            </div>
          </div>

          {/* Middle Ad - Removed to avoid content confusion */}

          {/* Related Events Section */}
          {relatedEvents.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Events</h2>
              <div className="overflow-x-auto">
                <div className="flex space-x-4 pb-4">
                  {relatedEvents.map((relatedEvent) => (
                    <div
                      key={relatedEvent.id}
                      onClick={() => navigate(`/events/${relatedEvent.id}`)}
                      className="flex-shrink-0 w-64 bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="aspect-w-16 aspect-h-9">
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                          {relatedEvent.images && relatedEvent.images.length > 0 ? (
                            <img
                              src={relatedEvent.images[0].original_image}
                              alt={relatedEvent.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-center text-gray-400">
                              <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{relatedEvent.name}</h3>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{formatDate(relatedEvent.date_time)}</span>
                          <div className="flex items-center text-sm text-gray-500">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {relatedEvent.views || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

                {/* Bottom Ad - Removed to avoid content confusion */}
              </div>
            </div>

            {/* Right Sidebar - Removed ads to avoid navigation confusion */}
          </div>
        </div>
      </div>
      {Lightbox}
    </PageLayout>
  );
};

export default EventDetailPage;